import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Box, Button, Drawer, Grid, Stack, Typography } from "@mui/material";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";
import { Range } from "react-date-range";
import { format } from "date-fns";

import { DeleteConfirmModal, DynamicTable, IconImage, PlaceholderCard, TradeDataExportModal, DateRangeFilter } from "@/components";
import { black_filter_img, sort_img, upload_img, delete_img, crash_warning } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  startPolling,
  stopPolling,
  tradeDataApi,
  useDeleteTradeDataMutation,
  useGetAllTradeDataQuery,
  useGetTradeDataColumnsQuery,
  useLazyGetAllTradeDataLogsQuery,
  useGetHTSCodesForDropdownQuery,
  useGetCountriesForDropdownQuery,
  useGetCommercialDescriptionsForDropdownQuery,
  usePostTradeDataMutation,
} from "@/services";
import { useSnackbar } from "@/hooks";
import { roles } from "@/roles";
import ProcessBox from "./ProcessBox";
import { getPollingInterval, formatDateOrTime, numberWithCommas } from "@/utils";
import {
  Backdrop,
  LeftFilters,
  RightFilters,
  StyledContainedButton,
  StyledDeleteContainedButton,
  StyledHeadeButtons,
  StyledMenuPopover,
} from "./style";
import TimelineData from "./Timeline";
import UploadData from "./upload-data";
import { IOSSwitch } from "@/components/switch/styles";

const INITIAL_PAGE = 1;
const EMPTY_COUNTRY_OPTIONS: { value: string; label?: string }[] = [];

const defaultVisibleColumns = [
  "entryLine",
  "entryDate",
  "countryOfOriginCode",
  "manufacturerID",
  "htsNumberFull",
  "lineTariffGoodsValueAmount",
  "totalLineDuties"
];

const allowedColumns = [
  "entryLine",
  "entryDate",
  "countryOfOriginCode",
  "manufacturerID",
  "htsNumberFull",
  "lineTariffGoodsValueAmount",
  "totalLineDuties",
  "filerCode",
  "portOfEntryCode",
  "entrySummaryDate",
  "entrySummaryCreateDate",
  "textileCategoryCode",
  "relatedPartyIndicator",
  "ch99CodeList",
  "mfnDuty",
  "duty201",
  "duty232",
  "duty301",
  "oth99Duties",
  "commercialDescription"
];

const columnLabels: Record<string, string> = {
  entryLine: "Entry Summary + Line",
  entryDate: "Entry Date",
  countryOfOriginCode: "Origin",
  manufacturerID: "Manufacturer ID",
  htsNumberFull: "Hts Number Full",
  lineTariffGoodsValueAmount: "Goods Value Amount",
  totalLineDuties: "Line Duty Amount",
  filerCode: "Filer Code",
  portOfEntryCode: "Port of Entry Code",
  entrySummaryDate: "Entry Summary Date",
  entrySummaryCreateDate: "Entry Summary Create Date",
  textileCategoryCode: "Textile Category Code",
  relatedPartyIndicator: "Related Party Indicator",
  ch99CodeList: "CH99 Code List",
  mfnDuty: "MFN Duties",
  duty201: "Section 201 Duties",
  duty232: "Section 232 Duties",
  duty301: "Section 301 Duties",
  oth99Duties: "Other 99 Duties",
  commercialDescription: "Commercial Description"
};

interface ColumnType extends TradeDataTypes {
  isHidden: boolean;
}

const AceDataView: FC = (): ReactElement => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [limit, setLimit] = useState(50);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "">("");
  const [sortType, setSortType] = useState<
    "entryDateSeconds" | "portOfEntryCode"  
  >("entryDateSeconds");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [columnsMenuAnchor, setColumnsMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  
  const [showLogs, setShowLogs] = useState(false);
  const [showUploadDataModal, setShowUploadDataModal] =
    useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const progressSubscriberRef = useRef<
    ((options: SubscriptionOptions) => void) | null
  >(null);

  const [dateFilter, setDateFilter] = useState<
    Pick<Range, "startDate" | "endDate">
  >({
    startDate: undefined,
    endDate: undefined,
  });

  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const dispatch = useAppDispatch();
  const { isPollingActive } = useAppSelector((state) => state.polling); // Get polling status from Redux store
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const { show } = useSnackbar();
  const [timeStamp, setTimeStamp] = useState<string>(new Date().toISOString());
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const shouldSkip = useMemo(
    () => {
      if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false; // Allow query to run for TDADMIN with TradeDive Client, even if selectedClient is not set
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, user]
  );

  const isAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  }, [user, selectedTenant]);

  const clientIdForCountryDropdown = useMemo(() => {
    // Service provider flows (SPOWNER/SPPARTNER/TDADMIN on SERVICE_PROVIDER) pick a client explicitly
    if (selectedClient?._id) return selectedClient._id;

    // TDCLIENT users are the "client" context; backend expects a clientId for this query
    if (user?.userRole === roles.TDCLIENT) return user._id;

    // TDADMIN on a TRADEDIVE_CLIENT tenant may not have a selectedClient; use the tenant id as the client context
    if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
      return selectedTenant?._id;
    }

    return undefined;
  }, [selectedClient?._id, user?._id, user?.userRole, selectedTenant?._id, selectedTenant?.tenantType]);

  const [fetchUploadProgress, { data: uploadProgress, error: progressError }] =
    useLazyGetAllTradeDataLogsQuery();

  // Fetch HTS Codes for Dropdown
  const { data: htsCodesData } = useGetHTSCodesForDropdownQuery(
    {
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
      includeChapter99: true,
    },
    {
      skip: shouldSkip,
    }
  );

  const { data: countryOptions = [] } = useGetCountriesForDropdownQuery(
    {
      clientId: clientIdForCountryDropdown as any,
      ...(dateFilter.startDate && { dateFrom: format(dateFilter.startDate, "yyyy-MM-dd") }),
      ...(dateFilter.endDate && { dateTo: format(dateFilter.endDate, "yyyy-MM-dd") }),
    },
    {
      skip: !clientIdForCountryDropdown,
    }
  );
  const countryOptionsStable = countryOptions ?? EMPTY_COUNTRY_OPTIONS;

  const { data: commercialDescriptionOptions = [] } = useGetCommercialDescriptionsForDropdownQuery(
    {
      clientId: clientIdForCountryDropdown as any,
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
      ...(dateFilter.startDate && { dateFrom: format(dateFilter.startDate, "yyyy-MM-dd") }),
      ...(dateFilter.endDate && { dateTo: format(dateFilter.endDate, "yyyy-MM-dd") }),
    },
    {
      skip: !clientIdForCountryDropdown,
    }
  );

  // Fetch columns dynamically
  const { data: columnsData, isLoading: isColumnsLoading } = useGetTradeDataColumnsQuery(
    {
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    },
    {
      skip: shouldSkip,
      refetchOnMountOrArgChange: true,
    }
  );

  // Construct columns from API data
  const dynamicColumns: IColumn<ColumnType>[] = useMemo(() => {
    if (!columnsData?.data || columnsData.data.length === 0) return [];

    // Filter available columns against the allowlist
    const availableColumns = columnsData.data.filter((key: string) => 
      allowedColumns.includes(key)
    );

    // Sort columns according to the order in allowedColumns
    availableColumns.sort((a, b) => {
      return allowedColumns.indexOf(a) - allowedColumns.indexOf(b);
    });

    return availableColumns.map((key: string) => {
      // Use explicit label if defined, otherwise fallback to Title Case
      const label = columnLabels[key] || key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase());

      const columnDef: any = {
        name: label,
        key: key as any,
        align: "center",
        minWidth: key === "countryOfOriginCode" ? "60px" : "120px",
        sorting: true, // Enable sorting for all dynamic columns
        isHidden: !defaultVisibleColumns.includes(key),
        component: (item: any) => {
          let value = item?.[key];
          const lowerKey = key.toLowerCase();
          const isDateColumn = lowerKey.includes('date');
          
          // Format dates if key contains "Date"
          if (isDateColumn && value) {
             // Try to parse if it's a number or string
             if (!isNaN(Number(value))) {
                value = formatDateOrTime(new Date(Number(value)), "date");
             } else if (!isNaN(Date.parse(value))) {
                value = formatDateOrTime(value, "date");
             }
          }

          // Format numbers with commas
          if (typeof value === 'number' && !isDateColumn) {
            value = numberWithCommas(value);
          }

          // Format array values (like ch99CodeList)
          if (Array.isArray(value)) {
            value = value.join(", ");
          }

          return (
            <Box style={{ whiteSpace: "nowrap" }}>
              {value ?? "-"}
            </Box>
          );
        },
      };

      // Add options for specific columns
      if (key === "htsNumberFull" && htsCodesData) {
        columnDef.filterOptions = htsCodesData
          .filter((item) => item.type === 'hts')
          .map((item) => item.value);
      }
      
      // Add Country Codes (static list or fetched if available, static for now as requested)
      if (key === "countryOfOriginCode") {
        columnDef.filterOptions =
          countryOptionsStable.length > 0
            ? countryOptionsStable.map((option) => option.value)
            : [];
      }

      // Add Commercial Description filter options with hybrid text filter
      if (key === "commercialDescription") {
        columnDef.filterOptions = commercialDescriptionOptions.map((option) => option.value);
        columnDef.allowTextFilter = true; // Enable hybrid mode (dropdown + text filter)
      }

      return columnDef;
    });
  }, [columnsData, htsCodesData, countryOptionsStable, commercialDescriptionOptions]);

  const [filteredColumns, setFilteredColumns] = useState<IColumn<ColumnType>[]>([]);

  // Update filtered columns when dynamic columns change
  useEffect(() => {
    if (dynamicColumns.length === 0) return;

    setFilteredColumns((prev) => {
      if (prev.length === 0) {
        return dynamicColumns;
      }

      const prevMap = new Map(
        prev.map((col) => [col.key, (col as any).isHidden ?? false])
      );

      const next = dynamicColumns.map((col) => {
        const previousHidden = prevMap.get(col.key);
        return {
          ...col,
          ...(previousHidden !== undefined && { isHidden: previousHidden }),
        };
      });

      // Avoid pointless state churn (and potential render loops) when nothing effectively changed.
      const sameStringArray = (a?: unknown, b?: unknown) => {
        const arrA = Array.isArray(a) ? (a as any[]) : undefined;
        const arrB = Array.isArray(b) ? (b as any[]) : undefined;
        if (!arrA && !arrB) return true;
        if (!arrA || !arrB) return false;
        if (arrA.length !== arrB.length) return false;
        for (let i = 0; i < arrA.length; i++) {
          if (arrA[i] !== arrB[i]) return false;
        }
        return true;
      };

      const isSame =
        prev.length === next.length &&
        prev.every((p, idx) => {
          const n: any = next[idx] as any;
          const pAny: any = p as any;
          // NOTE: include filterOptions so async-loaded dropdown options are not dropped.
          return (
            p.key === n.key &&
            (pAny.isHidden ?? false) === (n.isHidden ?? false) &&
            p.name === n.name &&
            p.align === n.align &&
            p.minWidth === n.minWidth &&
            Boolean(p.sorting) === Boolean(n.sorting) &&
            sameStringArray(pAny.filterOptions, n.filterOptions)
          );
        });

      return isSame ? prev : next;
    });
  }, [dynamicColumns]);

  const normalizeFilterValue = (value: any) => {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed ? trimmed : undefined;
    }
    if (typeof value === "object") {
      if ("value" in value) {
        let innerValue = value.value;
        if (Array.isArray(innerValue)) {
          innerValue = innerValue
            .map((item) => (typeof item === "string" ? item.trim() : item))
            .filter((item) => item !== undefined && item !== null && item !== "");
          if (innerValue.length === 0) return undefined;
        } else if (typeof innerValue === "string") {
          innerValue = innerValue.trim();
          if (!innerValue) return undefined;
        } else if (innerValue === null || innerValue === undefined) {
          return undefined;
        }
        return {
          ...value,
          value: innerValue,
        };
      }
      return value;
    }
    return value;
  };

  const handleColumnFilterChange = (key: string, value: any) => {
    setColumnFilters((prev) => {
      const nextFilters = { ...prev };
      const normalizedValue = normalizeFilterValue(value);
      if (normalizedValue === undefined) {
        delete nextFilters[key];
      } else {
        nextFilters[key] = normalizedValue;
      }
      return nextFilters;
    });
    setPage(1);
  };

  const handleClearFilters = () => {
    setColumnFilters({});
    setDateFilter({
      startDate: undefined,
      endDate: undefined,
    });
    setPage(INITIAL_PAGE);
  };

  const handleDateRangeSelect = useCallback(
    (range: Pick<Range, "startDate" | "endDate">) => {
      setDateFilter(range);
      setPage(INITIAL_PAGE);
    },
    [setPage]
  );

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTradeDataQuery(
    {
      pageSize: limit,
      pageNo: page,
      filters: columnFilters,
      ...(dateFilter.startDate && { dateFrom: format(dateFilter.startDate, "yyyy-MM-dd") }),
      ...(dateFilter.endDate && { dateTo: format(dateFilter.endDate, "yyyy-MM-dd") }),
      ...(sortOrder && { sortType: sortOrder === "asc" ? 1 : -1 }),
      ...(sortType && { sortBy: sortType }),
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const [postTradeData, { isLoading: isCreatingTradeData }] =
    usePostTradeDataMutation();

  const [deleteTradeData, { isLoading: isDeletingTradeData }] =
    useDeleteTradeDataMutation();
  const canUploadData = useMemo(() => {
    return (
      !!user &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDCLIENT, roles.TDADMIN].includes(user.userRole)
    );
  }, [user]);

  const filteredData = useMemo(() => {
    return (apiData?.getAllTradeData?.data || []).filter(
      (item: any) => item?.entryDate || item?.entryDateSeconds
    );
  }, [apiData]);

  const hasDateFilter = Boolean(dateFilter.startDate || dateFilter.endDate);
  const hasActiveFilters = useMemo(
    () => Object.keys(columnFilters).length > 0 || hasDateFilter,
    [columnFilters, hasDateFilter]
  );
  const totalRecords = apiData?.getAllTradeData?.meta?.countDocs ?? 0;
  const shouldShowFilteredEmptyState =
    !isLoading && hasActiveFilters && totalRecords === 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const showColumnsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (order: "asc" | "desc") => {
    setSortOrder(order);
    setPage(INITIAL_PAGE);
    handleClose();
  };

  const handleRequestSort = (property: string) => {
    const isDesc = sortType === property && sortOrder === "desc";
    setSortOrder(isDesc ? "asc" : "desc");
    setSortType(property as any);
  };

  const handleCol = (key: string) => {
    setFilteredColumns((prevFilteredColumns) => {
      return prevFilteredColumns.map((prevFilteredCol) => {
        if (key === prevFilteredCol.key) {
          return {
            ...prevFilteredCol,
            isHidden: !(prevFilteredCol as any).isHidden,
          };
        }

        return prevFilteredCol;
      });
    });
  };

  const handleDelete = async () => {
    const params: DeleteTradeDataVariables = {
      tenantId: "",
      clientId: ""
    };

    if (user?.userRole === roles.TDCLIENT) {
      params.tenantId = user.tenantId;
    } else if (user?.userRole && [roles.SPOWNER, roles.SPPARTNER].includes(user.userRole)) {
      params.tenantId = user?.tenantId ?? "";
      params.clientId = selectedClient?._id ?? "";
    } else if (user?.userRole === roles.TDADMIN) {
      params.tenantId = selectedTenant?._id ?? "";
      params.clientId = selectedClient?._id ?? "";
    }

   const res = await deleteTradeData(params);
   if(res.error) {
    show("Error deleting trade data", "", "error");
   } else {
    show("Trade data deleted successfully", "", "success");
    setDeleteModalOpen(false)
    dispatch(tradeDataApi.util.invalidateTags(["tradeData"]));
   }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const pullProgress = useCallback(async () => {
    // Determine polling interval based on current task status
    const pollingInterval = uploadProgress ? getPollingInterval([uploadProgress]) : 15000;

    const progressSubscriber = fetchUploadProgress({
      input: {
        clientId: selectedClient?._id ?? user?._id ?? "",
        uploadTime:timeStamp,
        ...(user?.userRole === roles.TDADMIN && selectedTenant && {
          tenantId: selectedTenant._id
        }),
      },
    });

    // Update subscription with adaptive polling interval
    progressSubscriber.updateSubscriptionOptions({ pollingInterval });

    dispatch(startPolling());
    progressSubscriberRef.current =
      progressSubscriber.updateSubscriptionOptions;
  }, [fetchUploadProgress, selectedClient, dispatch, timeStamp, uploadProgress, user, selectedTenant]);

  // page change polling
  useEffect(() => {
    if (isPollingActive) {
      pullProgress();
    }
  }, [isPollingActive, pullProgress]);

  // useEffect(() => {
  //   // skip the fetching if the client is not selected
  //   if (shouldSkip) return;
  //
  //   pullProgress();
  // }, [pullProgress, shouldSkip]);

  useEffect(() => {
    if (
      progressSubscriberRef.current &&
      (progressError || uploadProgress?.progress === 100)
    ) {
      progressSubscriberRef.current({ pollingInterval: 0 });
      progressSubscriberRef.current = null;
      dispatch(stopPolling());
    }

    if (uploadProgress?.progress === 100) {
      dispatch(tradeDataApi.util.invalidateTags(["tradeData"]));
      setPage(INITIAL_PAGE);
    }
  }, [uploadProgress, progressError, dispatch]);

  // Update polling interval based on task health
  useEffect(() => {
    if (progressSubscriberRef.current && uploadProgress) {
      const newPollingInterval = getPollingInterval([uploadProgress]);
      progressSubscriberRef.current({ pollingInterval: newPollingInterval });
    }
  }, [uploadProgress]);

  const createTradeDataFn = async (param: DropzoneTypes[]) => {
    if (param.length === 0) {
      return show("Please upload a file", "", "error");
    }

    const uploadedFile = param[0];
    if (!uploadedFile.fileUrl) {
      return show("File upload incomplete", "", "error");
    }

    const payload: CreateTradeDataDTO["tradeData"] = {
      tradeDataFile: uploadedFile.fileUrl,
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    };

    try {
      await postTradeData({
        tradeData: {
          ...payload,
        },
      }).unwrap();
      show("Data successfully uploaded", "", "success");
      setShowUploadDataModal(false);

      await pullProgress();
    } catch (error: any) {
      console.error("Upload error:", error);
      show(
        error?.data?.errors?.[0]?.message || 
        "Failed to upload data. Please try again.", 
        "", 
        "error"
      );
    }
  };

  return (
    <>
    {(shouldSkip && isAdmin)? (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#64748B" }}>Please select a client to view the trade data</Typography>
      </Stack>
    ) : (
      <Stack sx={{ flex: 1 }}>
        <StyledHeadeButtons container spacing={1.5}>
        <Grid item xs={12} md={12} lg={6}>
          <LeftFilters>
            <DateRangeFilter value={dateFilter} onSelectDate={handleDateRangeSelect} />
            <Box style={{ display: "none" }}>
              <StyledContainedButton
                size="small"
                title="Filter"
                variant="outlined"
                startIcon={<IconImage src={black_filter_img} size="auto" />}
              />
            </Box>

            <Box>
              <StyledContainedButton
                size="small"
                title="Sort"
                variant="outlined"
                onClick={handleClick}
                aria-describedby={id}
                sx={{ minWidth: 80 }}
                startIcon={<IconImage src={sort_img} size="auto" />}
              />
            </Box>
            <StyledContainedButton
              size="small"
              title="Show Columns"
              variant="outlined"
              onClick={showColumnsMenu}
              sx={{ minWidth: 130 }}
              startIcon={<IconImage src={black_filter_img} size="auto" />}
            />
            {filteredData.length > 0 && (
              <StyledDeleteContainedButton
                size="small"
                title={"Delete"}
                variant="outlined"
                onClick={() => setDeleteModalOpen(true)}
                sx={{ minWidth: 90 }}
                startIcon={<IconImage src={delete_img} size="16px" />}
              />
            )}
            <DeleteConfirmModal
              btnLoader={isDeletingTradeData} 
              open={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              maxWidth="xs"
              onConfirm={handleDelete}
              subText="history of trade data"
            />
          </LeftFilters>
          {!!columnsMenuAnchor && <Backdrop onClick={() => setColumnsMenuAnchor(null)} />}
          <StyledMenuPopover
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{ width: "231px" }}
            onClose={handleClose}
            open={open}
            anchorEl={anchorEl}
          >
            <Box>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  backgroundColor: sortOrder === "asc" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                onClick={() => handleSort("asc")}
                style={{ cursor: "pointer" }}
              >
                Ascending
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  backgroundColor: sortOrder === "desc" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                onClick={() => handleSort("desc")}
                style={{ cursor: "pointer" }}
              >
                Descending
              </Typography>
              <Typography
                onClick={() => {
                  setSortType("portOfEntryCode");
                  handleClose();
                  setPage(INITIAL_PAGE);
                }}
                sx={{
                  backgroundColor:
                    sortType === "portOfEntryCode" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                variant="body1"
                fontWeight={500}
              >
                Port of Entry
              </Typography>
              <Typography
                variant="body1"
                onClick={() => {
                  setSortType("entryDateSeconds");
                  handleClose();
                  setPage(INITIAL_PAGE);
                }}
                sx={{
                  backgroundColor:
                    sortType === "entryDateSeconds" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                fontWeight={500}
              >
                Entry Date
              </Typography>
            </Box>
          </StyledMenuPopover>
          <StyledMenuPopover
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{
              width: "400px",
              "& .MuiPaper-root": { maxHeight: "450px", overflow: "auto" },
            }}
            onClose={() => setColumnsMenuAnchor(null)}
            open={!!columnsMenuAnchor}
            anchorEl={columnsMenuAnchor}
          >
            <Stack>
              {filteredColumns.map((col) => {
                return (
                  <Stack
                    key={col.key}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      sx={{
                        cursor: "default !important",
                        background: "inherit !important",
                      }}
                    >
                      {col.name}
                    </Typography>
                    <IOSSwitch
                      disabled={(() => {
                        const enabledColumns = filteredColumns.filter(
                          (filCol: any) => !filCol.isHidden
                        );

                        const isLastColEnabled = enabledColumns.length === 1;

                        if (
                          isLastColEnabled &&
                          enabledColumns[0].key === col.key
                        ) {
                          return true;
                        }

                        return false;
                      })()}
                      checked={
                        filteredColumns.filter(
                          (filCol: any) =>
                            filCol.name === col.name && !filCol.isHidden
                        ).length > 0
                      }
                      onChange={() => handleCol(col.key)}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </StyledMenuPopover>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
           
            <RightFilters>
              <Box>
                <StyledContainedButton
                  size="small"
                  sx={{width:"140px"}}
                  title={"Export CSV"}
                  variant="outlined"
                  onClick={() => setShowExportModal(true)}
                />
              </Box>
              {canUploadData && (
                <StyledContainedButton
                  startIcon={<IconImage src={upload_img} size="auto" />}
                  sx={{width:"130px"}}
                  size="small"
                  title="Upload Data"
                  variant="contained"
                  onClick={() => setShowUploadDataModal(true)}
                />
              )}
            </RightFilters>
          </Stack>
        </Grid>
       
      </StyledHeadeButtons>
      {/* If the data exists from api also checks the data from api is 0 */}
      {(uploadProgress?.progress || uploadProgress?.progress === 0) &&
        uploadProgress.uploadTime !== null &&
        uploadProgress?.status === "inprocess" && (
          <ProcessBox
            progress={Math.round(uploadProgress.progress)}
            onClick={() => setShowLogs(true)}
            task={uploadProgress}
          />
        )}

      {/* Show error box when task failed */}
      {uploadProgress?.status === "error" && (
          <Box sx={{ maxWidth: 500, margin: "20px auto" }}>
            <PlaceholderCard
              title="Task Failed"
              description="An error occurred while processing your data. Please check your file and try again."
              imgUrl={crash_warning}
              isBordered
              centerContent
              cardStyles={{
                backgroundColor: '#fef2f2',
                borderColor: '#fecaca',
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center', 
              marginTop: 2
            }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  // Clear the upload progress by resetting the timestamp
                  // This causes the query to not match the failed upload
                  setTimeStamp(new Date().toISOString());
                }}
                sx={{ color: '#666' }}
              >
                Dismiss
              </Button>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => setShowUploadDataModal(true)}
                sx={{ 
                  backgroundColor: '#d32f2f',
                  '&:hover': { backgroundColor: '#b71c1c' }
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )}
     
      {shouldShowFilteredEmptyState && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
          action={
            <Button color="inherit" size="small" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          }
        >
          No results match the current filters.
        </Alert>
      )}

      <DynamicTable
        data={filteredData as TradeDataTypes[]}
        columns={filteredColumns
          .filter((column) => !(column as any).isHidden)
          .map((col) => {
            const { isHidden, ...rest } = col as any;

            return rest as IColumn<TradeDataTypes>;
          })}
        isLoading={isLoading || isFetching || isColumnsLoading} // show the loader if the client is not selected
        handleLimitChange={(e: number) => {
          setPage(INITIAL_PAGE);
          setLimit(e);
        }}
        sx={{
          flex: 1,
          "& tbody tr": {
            cursor: "auto ",
          },
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#E6ECED !important",
            color: "#030712 !important",
            fontWeight: "500 !important",
            fontSize: "12px !important",
            padding: "6px 10px !important",
            height: "28px !important",
          },
          "& .MuiTableHead-root .MuiTableCell-root .MuiBox-root": {
            color: "#030712 !important",
            fontWeight: "500 !important",
            fontSize: "12px !important",
          },
          "& .MuiTableBody-root .MuiTableCell-root": {
            fontSize: "12px !important",
            padding: "10px 10px !important",
          },
        }}
        handlePageChange={handlePageChange}
        stickyHeader
        hideScrollBars
        limit={limit}
        page={page}
        total={apiData?.getAllTradeData?.meta.countDocs ?? 0}
        hidePagination={false}
        sortOrder={sortOrder as "asc" | "desc"}
        sortBy={sortType}
        onRequestSort={handleRequestSort}
        filters={columnFilters}
        onFilterChange={handleColumnFilterChange}
      />

      <Drawer anchor="right" open={showLogs} onClose={() => setShowLogs(false)}>
        <TimelineData />
      </Drawer>
      {showUploadDataModal && (
        <UploadData
          title="Upload Trade Data"
          subTitle="Upload your ACE files so we can compile and structure your trade data for analysis"
          show={showUploadDataModal}
          setShow={setShowUploadDataModal}
          buttonText="Confirm"
          buttonAction={(e) => createTradeDataFn(e)}
          btnLoader={isCreatingTradeData}
          setTimeStamp={setTimeStamp}
        />
      )}
      {showExportModal && (
        <TradeDataExportModal show={showExportModal} setShow={setShowExportModal} />
      )}
    </Stack>
    )}
    </>
  );
};

export default AceDataView;

